import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import {
  API_PATH,
  BARID_API_VERSION,
  BARID_BASE_URL,
  BARID_ENABLED,
  BARID_TIMEOUT_MS
} from 'src/environments/environment';
import {
  BARID_LOCAL_AGENT_TICKET_HEADER,
  BARID_LOCAL_AGENT_TICKET_MESSAGES,
  BARID_LOCAL_AGENT_TICKET_RENEWABLE_CODES,
  BaridAgentAction,
  BaridAgentError,
  BaridAgentStatus,
  BaridCommandContext,
  BaridCommandRequest,
  BaridCommandResponse,
  BaridDiscoverPrintersResult,
  BaridEventsAuthRequest,
  BaridPanelTicketResult,
  BaridPrintPayload,
  BaridTefDeviceConfig,
  isBaridTicketRejectionCode
} from './barid-contracts';
import { BaridLocalTicketService, BaridTicketGrant, BaridTicketScope } from './barid-local-ticket.service';

const STORAGE_KEY = '@wazir:barid_settings';

export const BARID_CONFIG_LOADING_MESSAGE =
  'O Barid ainda está carregando a configuração deste computador. Aguarde alguns segundos e tente novamente.';

interface BaridLocalSettings {
  /** Credencial devolvida pelo agente no /pair; Bearer das rotas do loopback. */
  token?: string;
  tenantId?: string;
}

/** Credenciais do loopback numa tentativa: Bearer do pareamento e/ou ticket do agente local. */
interface BaridLoopbackAuth {
  token?: string;
  ticket?: BaridTicketGrant;
}

/** 1a mensagem do WS `/events` e por qual caminho ela autentica. */
export interface BaridEventsAuth {
  message: BaridEventsAuthRequest;
  via: 'bearer' | 'ticket';
}

/**
 * Cliente do Barid rodando NESTE computador (canal A, loopback
 * 127.0.0.1:47831). Usa `fetch` de proposito, fora do HttpClient: o
 * interceptor do Wazir injetaria o JWT do usuario e o x-tenant-id numa
 * chamada ao agente, e o agente nunca deve receber o JWT do Wazir. Tambem
 * evita o spinner e os toasts globais quando o agente nao esta instalado.
 *
 * Autenticacao das rotas protegidas (precedencia do agente): o Bearer do
 * pareamento feito NESTE navegador e, quando o agente aceita (contrato
 * 1.30.0), o ticket do agente local emitido pela nuvem
 * (`X-Barid-Agent-Ticket` + `x-tenant-id`) — "uma vez pareado, funciona de
 * qualquer navegador neste terminal". Sem ticket (API/agente antigo), so o
 * Bearer, como antes.
 */
@Injectable({ providedIn: 'root' })
export class BaridClientService {
  /** Sobrescrito nos testes. */
  fetchFn: typeof fetch = (...args) => fetch(...args);

  constructor(private readonly tickets: BaridLocalTicketService) {}

  get enabled(): boolean {
    return BARID_ENABLED;
  }

  get baseUrl(): string {
    return BARID_BASE_URL;
  }

  loadSettings(): BaridLocalSettings {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}');
    } catch {
      return {};
    }
  }

  saveSettings(settings: BaridLocalSettings): void {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }

  clearSettings(): void {
    window.localStorage.removeItem(STORAGE_KEY);
  }

  /** Ultimo `/status` lido (ou `null` quando o agente nao respondeu). */
  lastStatus: BaridAgentStatus | null = null;

  /**
   * Contrato 1.20: o agente ainda esta carregando o config.enc. Nesse
   * intervalo `paired` vem `false`, mas o pareamento salvo provavelmente
   * esta intacto: nada de parear de novo nem de esquecer a credencial.
   */
  get configLoading(): boolean {
    return this.lastStatus?.configLoading === true;
  }

  /**
   * Este navegador consegue falar com o agente pareado: tem a credencial do
   * pareamento OU um ticket valido em memoria.
   */
  get hasLocalAccess(): boolean {
    if (this.loadSettings().token) return true;
    const scope = this.ticketScope();
    return scope !== null && this.tickets.hasValidTicket(scope);
  }

  /** `/status` e publico no agente; `null` quando o Barid nao responde neste PC. */
  getStatus(): Observable<BaridAgentStatus | null> {
    return from(this.readStatus());
  }

  /**
   * Pareia o agente deste PC com o codigo gerado na API. O agente troca o
   * codigo em `context.apiUrl` e devolve a credencial, que passa a ser o
   * Bearer do loopback.
   */
  pair(pairingCode: string, tenantId: string): Observable<BaridCommandResponse<{ paired: boolean; token?: string }>> {
    if (this.configLoading) {
      // O agente recusaria (e um repareamento sobrescreveria o pareamento salvo).
      return from(Promise.reject(new BaridAgentError(BARID_CONFIG_LOADING_MESSAGE, 'CONFIG_LOADING')));
    }
    const request = this.buildRequest('PAIR', { token: pairingCode }, { tenantId, apiUrl: API_PATH });
    return from(
      this.request<BaridCommandResponse<{ paired: boolean; token?: string }>>('POST', '/pair', request, false).then(
        (response) => {
          if (response?.data?.token) {
            this.saveSettings({ token: response.data.token, tenantId });
          }
          // Re-pareamento muda o device (e talvez o tenant) do agente: ticket antigo nao vale mais.
          this.tickets.invalidate();
          return response;
        }
      )
    );
  }

  unpair(): Observable<BaridCommandResponse> {
    return from(
      this.request<BaridCommandResponse>('POST', '/unpair', this.buildRequest('PAIR', {}), false).finally(() => {
        this.clearSettings();
        this.tickets.invalidate();
      })
    );
  }

  discoverPrinters(scanNetwork = false): Observable<BaridCommandResponse<BaridDiscoverPrintersResult>> {
    return from(
      this.request<BaridCommandResponse<BaridDiscoverPrintersResult>>(
        'POST',
        '/printers/discover',
        { ...this.buildRequest('DISCOVER_PRINTERS', { scanNetwork }), scanNetwork },
        true,
        30_000
      )
    );
  }

  /** Envia ao Wazir so as impressoras marcadas (o agente sincroniza pela nuvem). */
  syncPrinters(printerIds: string[]): Observable<BaridCommandResponse> {
    return from(
      this.request<BaridCommandResponse>(
        'POST',
        '/printers/sync',
        { ...this.buildRequest('DISCOVER_PRINTERS', { printerIds }), printerIds },
        true,
        30_000
      )
    );
  }

  print(payload: BaridPrintPayload): Observable<BaridCommandResponse> {
    return from(this.request<BaridCommandResponse>('POST', '/print', this.buildRequest('PRINT', payload), true, 30_000));
  }

  applyTefConfig(tef: BaridTefDeviceConfig): Observable<BaridCommandResponse> {
    return from(
      this.request<BaridCommandResponse>('POST', '/tef/config', this.buildRequest('APPLY_TEF_CONFIG', { tef }), true, 15_000)
    );
  }

  /**
   * Pede (ou reaproveita) o ticket do agente local quando o agente pareado o
   * aceita. `false` = sem ticket (API antiga, computador nao pareado nesta
   * empresa, agente antigo): segue o comportamento de hoje. Nunca rejeita.
   */
  async ensureLocalTicket(): Promise<boolean> {
    const scope = this.ticketScope();
    if (!scope) return false;
    return Boolean(await this.tickets.getTicket(scope));
  }

  /**
   * Credencial para o WS `/events`. O Bearer do pareamento tem precedencia
   * (como nas rotas HTTP); sem ele, ou depois de o agente recusa-lo
   * (`bearerRejected`), vai o ticket. `null` = sem como autenticar (agente
   * nao pareado, antigo ou sem ticket): a tela segue so com o polling.
   */
  async eventsAuth(options: { bearerRejected?: boolean; forceNewTicket?: boolean } = {}): Promise<BaridEventsAuth | null> {
    if (!this.lastStatus?.paired) return null;
    const token = this.loadSettings().token;
    if (token && !options.bearerRejected) {
      return { message: { type: 'AUTH', authorization: `Bearer ${token}` }, via: 'bearer' };
    }
    const scope = this.ticketScope();
    if (!scope) return null;
    const grant = await this.tickets.getTicket(scope, { forceRefresh: options.forceNewTicket });
    return grant ? { message: { type: 'AUTH', ticket: grant.ticket }, via: 'ticket' } : null;
  }

  /** O agente recusou o ticket (WS ou HTTP): o proximo uso pede outro a nuvem. */
  invalidateLocalTicket(): void {
    this.tickets.invalidate();
  }

  /**
   * URL do painel do Barid deste computador. Com acesso local (pareamento ou
   * ticket) pede ao agente um ticket de painel de uso unico (`POST
   * /panel/ticket`, purpose `panel`, 60 s) que abre o painel ja autenticado;
   * sem acesso, ou em agente sem a rota, abre o `/status` (o proprio painel).
   */
  async localPanelUrl(): Promise<string> {
    const fallback = `${this.baseUrl}/status`;
    if (!this.hasLocalAccess && !(await this.ensureLocalTicket())) return fallback;
    try {
      const response = await this.request<BaridCommandResponse<BaridPanelTicketResult>>(
        'POST',
        '/panel/ticket',
        { purpose: 'panel' },
        true
      );
      const url = response?.data?.url;
      return url && this.isLoopbackPanelUrl(url) ? url : fallback;
    } catch {
      return fallback;
    }
  }

  buildRequest<TPayload>(action: BaridAgentAction, payload: TPayload, context?: BaridCommandContext): BaridCommandRequest<TPayload> {
    return {
      requestId: this.newRequestId(),
      apiVersion: BARID_API_VERSION,
      action,
      timestamp: new Date().toISOString(),
      context: { tenantId: this.loadSettings().tenantId, ...(context ?? {}) },
      payload
    };
  }

  /**
   * Chamada autenticada ao loopback do Barid para rotas do modo offline (`/offline/local/...`). So usada pelo
   * `OfflineInterceptor`, com a chave ligada e o Barid em OFFLINE. O JWT do Wazir nunca vai ao agente.
   */
  offlineCall<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    path: string,
    body?: unknown,
    extraHeaders: Record<string, string> = {}
  ): Promise<T> {
    return this.request<T>(method, path, body, true, BARID_TIMEOUT_MS, false, extraHeaders);
  }

  private readStatus(): Promise<BaridAgentStatus | null> {
    return this.request<BaridAgentStatus>('GET', '/status')
      .then((status) => (this.lastStatus = status ?? null))
      .catch(() => (this.lastStatus = null));
  }

  /** Computador pareado (status do agente) para pedir o ticket; `null` se o agente nao aceita ticket. */
  private ticketScope(): BaridTicketScope | null {
    const status = this.lastStatus;
    const machineId = status?.machineIdentity?.machineId;
    // Agente antigo (sem localAgentTicket) ou ainda sem a chave da nuvem (ready=false): so o Bearer.
    if (!status?.paired || !machineId || !status.localAgentTicket?.supported || status.localAgentTicket.ready === false) {
      return null;
    }
    const deviceId = status.pairingProfile?.deviceSettingId;
    return deviceId ? { machineId, deviceId } : { machineId };
  }

  /**
   * So aceita a URL de painel devolvida pelo agente se for da MESMA origem (protocolo, host e
   * porta) do agente que respondeu (`baseUrl`): qualquer outra porta de 127.0.0.1/localhost pode
   * ser outro processo local.
   */
  private isLoopbackPanelUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      const agent = new URL(this.baseUrl);
      return (
        parsed.origin === agent.origin && ['127.0.0.1', 'localhost'].includes(parsed.hostname)
      );
    } catch {
      return false;
    }
  }

  private async resolveAuth(forceNewTicket: boolean): Promise<BaridLoopbackAuth> {
    const token = this.loadSettings().token;
    if (!this.lastStatus && !token) {
      // Rota protegida antes de qualquer /status: le o status para saber se o agente aceita ticket.
      await this.readStatus();
    }
    const scope = this.ticketScope();
    const ticket = scope ? await this.tickets.getTicket(scope, { forceRefresh: forceNewTicket }) : null;
    return { ...(token ? { token } : {}), ...(ticket ? { ticket } : {}) };
  }

  private newRequestId(): string {
    const cryptoRef = (window as any).crypto;
    return typeof cryptoRef?.randomUUID === 'function'
      ? cryptoRef.randomUUID()
      : `barid-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  private async request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    path: string,
    body?: unknown,
    authenticated = false,
    timeoutMs = BARID_TIMEOUT_MS,
    ticketRetried = false,
    extraHeaders: Record<string, string> = {}
  ): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json', ...extraHeaders };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    let auth: BaridLoopbackAuth = {};
    if (authenticated) {
      auth = await this.resolveAuth(ticketRetried);
      if (!auth.token && !auth.ticket) {
        throw new BaridAgentError('Este computador ainda nao foi pareado com o Barid.', 'PAIRING_REQUIRED');
      }
      // Precedencia do agente: Bearer do pareamento primeiro; se nao bater, vale o ticket.
      if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`;
      if (auth.ticket) {
        headers[BARID_LOCAL_AGENT_TICKET_HEADER] = auth.ticket.ticket;
        headers['x-tenant-id'] = auth.ticket.tenantId;
      }
    }

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
    const timer = setTimeout(() => controller?.abort(), timeoutMs);
    let response: Response;
    try {
      response = await this.fetchFn(`${this.baseUrl}${path}`, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: controller?.signal
      });
    } catch {
      throw new BaridAgentError('O Barid nao respondeu neste computador. Verifique se ele esta instalado e em execucao.', 'AGENT_UNREACHABLE');
    } finally {
      clearTimeout(timer);
    }

    const json = await response.json().catch(() => undefined);
    if (!response.ok || json?.success === false) {
      const error = json?.error;
      const message = typeof error === 'string' ? error : error?.message || json?.message;
      // Recusas de autorizacao do agente vem como `{ error, code }` (code na raiz).
      const code = typeof error === 'object' && error?.code ? error.code : json?.code;

      if (auth.ticket && isBaridTicketRejectionCode(code)) {
        // A recusa e do ticket (o Bearer, se foi, tambem nao bateu): esquece e,
        // quando um ticket novo resolve, tenta mais uma vez em silencio. A
        // requisicao recusada na autorizacao nao foi executada pelo agente.
        this.tickets.invalidate();
        if (!ticketRetried && BARID_LOCAL_AGENT_TICKET_RENEWABLE_CODES.includes(code)) {
          return this.request<T>(method, path, body, authenticated, timeoutMs, true, extraHeaders);
        }
        throw new BaridAgentError(message || BARID_LOCAL_AGENT_TICKET_MESSAGES[code], code, response.status);
      }

      // Durante o carregamento do config.enc o agente ainda nao conhece a
      // credencial: um 401 ali nao significa credencial recusada.
      if (response.status === 401 && code !== 'PAIRING_REQUIRED' && !this.configLoading) {
        this.clearSettings();
      }
      throw new BaridAgentError(message || `O Barid recusou a operacao (${response.status}).`, code, response.status);
    }
    return json as T;
  }
}
